// features/cx/payments/hooks.ts

import { useSmartQuery, useSmartMutation } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useState, useCallback } from "react";
import { paymentsApi } from "./api";
import {
  ConsumerSearchFilters,
  PaymentFilters,
  CreditNoteFilters,
  CreatePaymentPayload,
  CreateCreditNotePayload,
  PaymentExtraData,
} from "./types";
import { toast } from "@shared/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { queryClient } from "@shared/api/queries/queryClient";
import { downloadCSV } from "@shared/utils/utils";

// ================================
// CONSUMER SEARCH HOOKS
// ================================

/**
 * Search consumers for payments
 */
export const useConsumerSearch = (filters: ConsumerSearchFilters) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.consumerSearch(filters),
    () => paymentsApi.searchConsumer(filters),
    {
      enabled: filters.searchData.length >= 3,
      staleTime: 5 * 60 * 1000,
      retry: false,
    }
  );
};

/**
 * Get consumer meta data
 */
export const useConsumerMetaData = (params: {
  consumerId: number;
  remoteUtilityId: number;
  enabled: boolean;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.consumerMetaData(
      params.consumerId,
      params.remoteUtilityId
    ),
    () => paymentsApi.getConsumerMetaData(params),
    {
      enabled: params.enabled,
    }
  );
};

// ================================
// SUMMARY HOOKS
// ================================

/**
 * Get payment management summary
 */
export const usePaymentManagementSummary = (remoteUtilityId: number) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.summary(remoteUtilityId),
    () => paymentsApi.getPaymentManagementSummary(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
    }
  );
};

/**
 * Get credit note summary
 */
export const useCreditNoteSummary = (remoteUtilityId: number) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.creditNoteSummary(remoteUtilityId),
    () => paymentsApi.getCreditNoteSummary(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
    }
  );
};

// ================================
// CONSUMER BILL HOOKS
// ================================

/**
 * Get latest consumer bill
 */
export const useLatestConsumerBill = (params: {
  remoteUtilityId: number;
  consumerId: number;
  consumerNo: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.latestBill(
      params.remoteUtilityId,
      params.consumerId,
      params.consumerNo
    ),
    () => paymentsApi.getLatestConsumerBill(params),
    {
      enabled:
        !!params.remoteUtilityId && !!params.consumerId && !!params.consumerNo,
      staleTime: 2 * 60 * 1000,
    }
  );
};

/**
 * Get payment services
 */
export const usePaymentServices = (params: {
  remoteUtilityId: number;
  consumerId: number;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.services(params),
    () => paymentsApi.getPaymentServices(params),
    {
      enabled: !!params.remoteUtilityId && !!params.consumerId,
      staleTime: 5 * 60 * 1000,
    }
  );
};

// ================================
// PAYMENT HOOKS
// ================================

/**
 * Get payments list
 */
export const usePayments = (filters: PaymentFilters) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.list(filters),
    () => paymentsApi.getPayments(filters),
    {
      enabled: !!filters.remoteUtilityId,
      staleTime: 2 * 60 * 1000,
    }
  );
};

 export const useServiceOrder = (params: {
    remote_utility_id: any;
    consumer_id:any;
  }) => {
    return useSmartQuery(
      QueryKeyFactory.module.cx.payments.serviceOrderData(params),
      () => paymentsApi.getServiceOrder(params)
    );
  };

/**
 * Export payments list
 */
export const usePaymentExport = (filters: PaymentFilters) => {
  const [isExporting, setIsExporting] = useState(false);

  const exportMutation = useSmartQuery(
    QueryKeyFactory.module.cx.payments.exportList(filters),
    async () => {
      setIsExporting(true);
      try {
        const blob = await paymentsApi.exportPaymentsList(filters);
        
        // Use the reusable download utility
        toast({
          title: "Export Initiated",
          description: blob?.message,
          variant: "default",
        });
        return { success: true };
      } catch (error: any) {
        toast({
          title: `Failed to export payments: ${error.message}`,
          variant: "destructive",
        });
        throw error;
      } finally {
        setIsExporting(false);
      }
    },
    {
      enabled: false,
    }
  );

  const exportCSV = () => {
    exportMutation.refetch();
  };

  return {
    exportCSV,
    isExporting: isExporting || exportMutation.isFetching,
    error: exportMutation.error,
    isError: exportMutation.isError,
  };
};

/**
 * Get payment detail
 */
export const usePaymentDetail = (params: {
  paymentId: number;
  remoteUtilityId: number;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.details(params.paymentId),
    () => paymentsApi.getPaymentDetail(params),
    {
      enabled: !!params.paymentId && !!params.remoteUtilityId,
      staleTime: 2 * 60 * 1000,
    }
  );
};

/**
 * Get credit note detail
 */
export const useCreditNoteDetail = (params: {
  creditNoteId: number;
  remoteUtilityId: number;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.creditNoteDetails(params.creditNoteId),
    () => paymentsApi.getCreditNoteDetail(params),
    {
      enabled: !!params.creditNoteId && !!params.remoteUtilityId,
    }
  );
};

/**
 * Create payment
 */
export const useCreatePayment = (
  remoteUtilityId: number,
  consumer: number,
  consumerNo: string
) => {
  const navigate = useNavigate();
  return useSmartMutation(
    (payload: CreatePaymentPayload) => paymentsApi.createPayment(payload),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.payments.list(),
        QueryKeyFactory.module.cx.payments.summary(remoteUtilityId),
        QueryKeyFactory.module.cx.payments.latestBill(
          remoteUtilityId,
          consumer,
          consumerNo
        ),
      ],
      onSuccess: (data, variables) => {
        navigate("/cx/payments/success", {
          state: { ...data, formData: variables },
        });


        // Invalidate only the 'cxonboarding' -> 'consumerList' queries
        queryClient.refetchQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey[0] === "cxonboarding" &&
            query.queryKey[1] === "consumerList",
        });
      },
      onError: (error: any) => {
        toast({
          title: `Failed to create payment: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

/**
 * Update payment
 */
export const useUpdatePayment = (
  paymentId: number,
  remoteUtilityId: number
) => {
  return useSmartMutation(
    (payload: Partial<CreatePaymentPayload>) =>
      paymentsApi.updatePayment(paymentId, payload),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.payments.details(paymentId),
        QueryKeyFactory.module.cx.payments.list(),
        QueryKeyFactory.module.cx.payments.summary(remoteUtilityId),
      ],
      onSuccess: () => {
        toast({
          title: "Payment updated successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: `Failed to update payment: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

/**
 * Delete payment
 */
export const useDeletePayment = (remoteUtilityId: number) => {
  return useSmartMutation(
    (paymentId: number) => paymentsApi.deletePayment(paymentId),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.payments.list(),
        QueryKeyFactory.module.cx.payments.summary(remoteUtilityId),
      ],
      onSuccess: () => {
        toast({
          title: "Payment deleted successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: `Failed to delete payment: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

// ================================
// CREDIT NOTE HOOKS
// ================================

/**
 * Get credit notes list
 */
export const useCreditNotes = (filters: CreditNoteFilters) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.creditNotes(filters),
    () => paymentsApi.getCreditNotes(filters),
    {
      enabled: !!filters.remoteUtilityId,
      staleTime: 2 * 60 * 1000,
    }
  );
};

export const useCreditNoteExport = (filters: CreditNoteFilters) => {
  const [isExporting, setIsExporting] = useState(false);

  const exportMutation = useSmartQuery(
    QueryKeyFactory.module.cx.payments.creditNotes(filters),
    async () => {
      setIsExporting(true);
      try {
        const blob = await paymentsApi.exportCreditNotesList(filters);
        toast({
          title: "Export Initiated",
          description: blob?.message,
          variant: "default",
        });
        
        return { success: true };
      } catch (error: any) {
        toast({
          title: `Failed to export credit notes: ${error.message}`,
          variant: "destructive",
        });
        throw error;
      } finally {
        setIsExporting(false);
      }
    },
    {
      enabled: false,
    }
  );

  const exportCSV = () => {
    exportMutation.refetch();
  };

  return {
    exportCSV,
    isExporting: isExporting || exportMutation.isFetching,
    error: exportMutation.error,
    isError: exportMutation.isError,
  };
};
/**
 * Create credit note
 */
export const useCreateCreditNote = (remoteUtilityId: number) => {
  return useSmartMutation(
    (payload: CreateCreditNotePayload) => paymentsApi.createCreditNote(payload),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.payments.creditNotes(),
        QueryKeyFactory.module.cx.payments.creditNoteSummary(remoteUtilityId),
        QueryKeyFactory.module.cx.payments.list(),
      ],
      onSuccess: () => {
        toast({
          title: "Credit note created successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: `Failed to create credit note: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

// ================================
// CHOICE HOOKS
// ================================

/**
 * Get credit note reasons
 */
export const useCreditNoteReasons = (remoteUtilityId: number) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.creditNoteReasons(remoteUtilityId),
    () => paymentsApi.getCreditNoteReasons(),
    {
      enabled: !!remoteUtilityId,
      staleTime: 30 * 60 * 1000, // 30 minutes
    }
  );
};

/**
 * Get payment received status choices
 */
export const usePaymentChannelChoices = (remoteUtilityId: number) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.paymentChannelChoices(remoteUtilityId),
    () => paymentsApi.getPaymentChannelChoices(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
      staleTime: 30 * 60 * 1000, // 30 minutes
    }
  );
};

/**
 * Get payment pay type choices
 */
export const usePaymentPayTypeChoices = (remoteUtilityId: number) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.paymentPayTypeChoices(remoteUtilityId),
    () => paymentsApi.getPaymentPayTypeChoices(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
      staleTime: 30 * 60 * 1000, // 30 minutes
    }
  );
};

/**
 * Get payment received status choices
 */
export const usePaymentReceivedStatusChoices = (remoteUtilityId: number) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.paymentReceivedStatusChoices(
      remoteUtilityId
    ),
    () => paymentsApi.getPaymentReceivedStatusChoices(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
      staleTime: 30 * 60 * 1000, // 30 minutes
    }
  );
};

export const useInstallments = (params: {
  remoteUtilityId: number;
  consumerId: number;
  consumerNo: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.installments(params),
    () => paymentsApi.getInstallments(params),
    {
      staleTime: 30 * 60 * 1000, // 30 minutes
    }
  );
};

export const usePaymentMethod = (params: { remoteUtilityId: number }) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.payments.paymentMethod(params),
    () => paymentsApi.getPaymentMethod(params),
    {
      enabled: !!params.remoteUtilityId,
      staleTime: 30 * 60 * 1000, // 30 minutes
    }
  );
};
